import React, { useEffect, useMemo, useState } from 'react'
import { isBrandPlan, isNetworkPlan } from '../../utils/plans'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import axiosInstant, { setActiveRmnBrandId } from '../../api'
import { brandBySlugEndpoint, publicRmnBrandBySlugEndpoint } from '../../api/endpoints'
import { useUser } from '../../hooks/useUser'
import { RmnPublicBrand } from '../../types/rmn'
import { resolveRmnPalette } from '../../utils/color'
import { DotLottleLoader } from '../../components/Loader'
import { EdgeShell } from '../../components/SideNav/EdgeShell'
import { RmnBrandContext } from './RmnBrandContext'

// Route element for /portal/:brandSlug/* — the brand portal, canonical for
// brand clients since TDC-1609 and the workspace internal staff open for a
// brand. Resolves the slug, provides brand + palette context, and renders the
// child routes: logged-in users get the AppShell rail via EdgeShell (internal
// staff see the workspace rail — see sideNavModel); logged out, the branded
// login renders with NO shell, so nothing TDC-branded frames a white-label
// front door.
//
// Two resolvers: logged-in viewers use the authenticated GET
// /brands/slug/:slug (any active brand, no association graph); logged-out
// visitors use the public GET /public/rmn/:slug, which since TDC-1609 resolves
// ANY active brand — every brand has a branded login now, not just networks.
// A slug the public resolver can't see sends a logged-out visitor to the
// standard login and back here; a slug nobody can resolve lands on /404. Internal
// staff's /rmn/* API calls need the rmn_brand_id param this layout
// registers.

const toPortalBrand = (data: any): RmnPublicBrand => ({
  id: data.id,
  name: data.name,
  slug: data.slug,
  logo: data.logo,
  rmn: data.rmn === true || isNetworkPlan(data.plan),
  plan: isBrandPlan(data.plan) ? data.plan : null,
  max_products: data.max_products === undefined ? null : data.max_products,
  max_pdp_score_interval: data.max_pdp_score_interval || null,
  max_cv_analysis_interval: data.max_cv_analysis_interval || null,
  max_aip_analysis_interval: data.max_aip_analysis_interval || null,
  rmn_settings: data.rmn_settings || {},
  site_url: data.site_url || null,
  allowed_domains: Array.isArray(data.allowed_domains) ? data.allowed_domains : null,
})

export function RmnBrandLayout() {
  const { brandSlug } = useParams()
  const { pathname, search } = useLocation()
  const { user } = useUser()
  const loggedIn = !!user
  const [brand, setBrand] = useState<RmnPublicBrand | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound'>('loading')

  useEffect(() => {
    let mounted = true
    setStatus('loading')
    const endpoint = loggedIn
      ? brandBySlugEndpoint(String(brandSlug))
      : publicRmnBrandBySlugEndpoint(String(brandSlug))
    axiosInstant
      .get(endpoint)
      .then((res) => {
        if (!mounted) return
        const resolved = toPortalBrand(res.data)
        setBrand(resolved)
        setStatus('ready')
        // NOTE: the branded-login slug (RMN_SLUG) is deliberately NOT written
        // here any more (TDC-1609). It is written once from /me in
        // store/user.ts, so it always names the viewer's OWN brand. Writing it
        // from the layout would also stamp it for internal staff merely
        // visiting a workspace, and their next 401 would drop them on that
        // client's branded login instead of /login.
      })
      .catch(() => {
        if (mounted) setStatus('notfound')
      })
    return () => {
      mounted = false
    }
  }, [brandSlug, loggedIn])

  // Register the resolved brand with the API client so authenticated
  // /rmn/* calls carry rmn_brand_id (required for internal staff — see
  // setActiveRmnBrandId). Cleared when the user leaves the portal.
  useEffect(() => {
    if (!brand) return undefined
    setActiveRmnBrandId(brand.id)
    return () => setActiveRmnBrandId(null)
  }, [brand])

  const palette = useMemo(
    () => resolveRmnPalette(brand?.rmn_settings, brand?.name),
    [brand]
  )
  const contextValue = useMemo(() => ({ brand, palette, loading: false }), [brand, palette])

  if (status === 'notfound') {
    if (loggedIn) return <Navigate to="/404" replace />
    const redirect = encodeURIComponent(pathname + search)
    return <Navigate to={`/login?redirect=${redirect}`} replace />
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DotLottleLoader />
      </div>
    )
  }

  // Logged out, the only page reachable here is the branded login (RmnAuthGuard
  // sends everything else to it), and it brings its own chrome — brand logo,
  // system name, brand colors, its own "Powered by The Desire Company" line.
  // EdgeShell would wrap that in the TDC app bar (logo linking out to /) and
  // the TDC footer, which is exactly the branding a white-label front door is
  // supposed to replace.
  if (!loggedIn) {
    return (
      <RmnBrandContext.Provider value={contextValue}>
        <div className="min-h-screen flex flex-col">
          <Outlet />
        </div>
      </RmnBrandContext.Provider>
    )
  }

  return (
    <RmnBrandContext.Provider value={contextValue}>
      <EdgeShell>
        <Outlet />
      </EdgeShell>
    </RmnBrandContext.Provider>
  )
}
